/**
 * Filtro de ruído aplicado na saída para o Sentry: descarta erros que nasceram
 * em script INJETADO no WebView por um app hospedeiro, não no código do app.
 *
 * WebViews Android de apps injetores (SnapTube e afins) expõem métodos nativos
 * via `addJavascriptInterface` e avaliam script próprio na página para detetar
 * mídia. Esse script roda na MESMA origem do documento, então:
 *
 *  - `denyUrls` não alcança — a origem do frame é `app:///<anonymous>`, e não
 *    `chrome-extension://` nem `blob:`;
 *  - o erro sobe pelo `window.onerror` (mechanism `onerror`), sem passar pelo
 *    GlobalErrorHandler nem pelo SentryErrorInterceptor;
 *  - a stack atravessa o zone.js, porque o zone faz patch do `setTimeout`
 *    global e QUALQUER script da página passa por lá. Os frames de zone.js são
 *    transporte, não origem — o Sentry marca `callback.apply` como suspeito e a
 *    issue acaba atribuída ao app.
 *
 * O caso concreto (TREINO-80, `SyntaxError: "undefined" is not valid JSON` em
 * /training/:id, Android): a chamada Java devolveu `null`, virou a string
 * `"undefined"` na travessia da ponte, e o script injetado fez `JSON.parse`
 * nela sem guarda. O usuário não perde nada — os breadcrumbs mostram o treino
 * sendo finalizado normalmente depois do erro.
 *
 * POR QUE NÃO FILTRAR PELA MENSAGEM: `"undefined" is not valid JSON` é genérica
 * e o app tem dezenas de `JSON.parse` próprios. Uma entrada em `ignoreErrors`
 * engoliria também um erro legítimo de parse nosso. O discriminador tem de ser
 * a ORIGEM, por isso o corte acontece aqui, pelos frames.
 *
 * O corte é estreito de propósito: exige o símbolo do injetor E o ficheiro
 * anônimo NO MESMO frame. Na dúvida, o evento é enviado.
 */

/**
 * Símbolos das pontes JS↔Java conhecidas, como aparecem em `frame.function`.
 * Os valores vistos em produção são `Object.jsReceiveMessages` e
 * `SnapTube.value`, daí a busca por substring em vez de igualdade.
 *
 * Nenhum destes nomes existe no código do app nem nas dependências — o grep em
 * `src/`, `www/`, `android/` e `ios/` não os encontra. É o que torna seguro
 * cortar por eles.
 */
const INJECTOR_FUNCTION_SYMBOLS = ['jsReceiveMessages', 'SnapTube'];

/**
 * Marca de script sem ficheiro de origem, produzida por `evaluateJavascript`.
 * Os frames reais do app são `app:///main-<hash>.js` ou `./node_modules/...`,
 * então esta condição sozinha já separa código avaliado de código carregado.
 */
const ANONYMOUS_SCRIPT_MARKER = '<anonymous>';

/** Recorte estrutural do evento: só a stack é lida daqui. */
interface ISentryEventShape {
  exception?: {
    values?: {
      stacktrace?: {
        frames?: { function?: string; filename?: string }[];
      };
    }[];
  };
}

/** `true` quando o frame é um símbolo de ponte nativa avaliado na página. */
function isInjectorFrame(frame: { function?: string; filename?: string }): boolean {
  const nomeDaFuncao = frame.function;
  const ficheiro = frame.filename;
  if (!nomeDaFuncao || !ficheiro) return false;

  // As duas condições valem para o MESMO frame: um símbolo de injetor num
  // ficheiro nosso (improvável, mas seria sinal real) continua sendo enviado.
  if (!ficheiro.includes(ANONYMOUS_SCRIPT_MARKER)) return false;

  return INJECTOR_FUNCTION_SYMBOLS.some((simbolo) => nomeDaFuncao.includes(simbolo));
}

/**
 * `true` quando o evento nasceu em script injetado por app hospedeiro e pode
 * ser descartado. Percorre todas as exceções da cadeia porque o erro relevante
 * pode ser uma causa encadeada, não só a primeira.
 */
export function isInjectedWebViewScriptError(event: ISentryEventShape): boolean {
  const values = event.exception?.values;
  if (!values?.length) return false;

  return values.some((value) => value.stacktrace?.frames?.some(isInjectorFrame) ?? false);
}
